import { Divider, Show } from '@chakra-ui/react';
import React from 'react';

import { PermissionGuard } from '~/core/PermissionGuard';
import { permissions as analyticsPermissions } from '~/core/routes/analytics/Router';
import { permissions as calcTermsPermissions } from '~/core/routes/calculation-terminologies/_index';
import { permissions as cdqPermissions } from '~/core/routes/collateral-data-quality/_index';
import { permissions as dataPipelinePermissions } from '~/core/routes/data-pipeline/Router';
import * as DeliverablesEntityConfig from '~/core/routes/deliverables/EntityConfig';
import { permissions as eventsPermissions } from '~/core/routes/events/_index';
import * as FundingRequestsEntityConfig from '~/core/routes/funding-requests/EntityConfig';
import { permissions as implementationsPermissions } from '~/core/routes/implementations/Router';
import * as InvoicesEntityConfig from '~/core/routes/invoices/EntityConfig';
import * as LimitedPartnersEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import { permissions as loansPermissions } from '~/core/routes/loans/permissions';
import { permissions as omniAnalyticsPermissions } from '~/core/routes/omni-analytics/_index';
import { permissions as reportsPermissions } from '~/core/routes/reports/_index';
import { permissions as settingsPermissions } from '~/core/routes/settings/Router';
import { TasksGlobalNavRouterLinkWithUpsell } from '~/core/routes/tasks/TasksGlobalNavRouterLinkWithUpsell';
import { permissions as transactionsPermissions } from '~/core/routes/transactions/_index';
import { valleyPermissions as valleyBankAdvanceRatePermissions } from '~/core/routes/valley-bank/advance-rate/_index';
import { valleyPermissions as valleyBankAssignmentPermissions } from '~/core/routes/valley-bank/assignment/_index';
import * as ValleyBankEntityConfig from '~/core/routes/valley-bank/EntityConfig';
import { useValleyGlobalNavConfig } from '~/core/routes/valley-bank/useValleyGlobalNavConfigs';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  GlobalNav,
  GlobalNavRouterLinkItemWithUpsell,
} from '~/packages/common-frontend/layout';

// Exports
export const CoreGlobalNav = ({ isFixed = true }: { isFixed?: boolean }) => {
  const valleyConfig = useValleyGlobalNavConfig();

  return (
    <GlobalNav
      isFixed={isFixed}
      offsetHeaderCount={valleyConfig.hasSubheader ? 2 : 1}
    >
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType !== 'CAPITAL_PROVIDER') return 'deny';
          return 'allow';
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/portfolio',
          label: 'Portfolio',
          icon: <Icon icon="house" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return 'allow';
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/',
          end: true,
          label: 'Home',
          icon: <Icon icon="house" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={LimitedPartnersEntityConfig.permissions}
        GlobalNavRouterLinkItemProps={{
          to: '/limited-partners',
          label: 'LPs',
          icon: <Icon icon="circle-dollar-to-slot" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return context.flags.productsCreditAgreementsPermissions;
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/credit-agreements',
          label: 'Credit Agreements',
          icon: <Icon icon="file-lines" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={reportsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/reports',
          label: 'Reports',
          icon: <Icon icon="folder-arrow-down" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return context.flags.productsReceivablesPermissions;
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/credit-facility-receivables',
          label: 'Receivables',
          icon: <Icon icon="list" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return context.flags.productsAssignmentsPermissions;
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/assignments',
          label: 'Assignments',
          icon: <Icon icon="sliders-up" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={FundingRequestsEntityConfig.permissions}
        GlobalNavRouterLinkItemProps={{
          to: '/funding-requests',
          label: 'Funding Requests',
          icon: <Icon icon="dollar-sign" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={InvoicesEntityConfig.permissions}
        GlobalNavRouterLinkItemProps={{
          to: '/invoices',
          label: 'Invoices',
          icon: <Icon icon="file-invoice-dollar" />,
        }}
      />

      <TasksGlobalNavRouterLinkWithUpsell />

      <GlobalNavRouterLinkItemWithUpsell
        permissions={cdqPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/collateral-data-quality',
          label: 'Collateral Data Monitoring',
          icon: <Icon icon="monitor-waveform" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return context.flags.productsStratsPermissions;
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/strats',
          label: 'Strats',
          icon: <Icon icon="chart-mixed" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return context.flags.productsInsightsPermissions;
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/insights',
          label: 'Insights',
          icon: <Icon icon="chart-pie-simple" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={analyticsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/analytics',
          label: 'Analytics',
          icon: <Icon icon="chart-pie-simple" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={omniAnalyticsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/omni-analytics',
          label: 'Analytics V2',
          icon: <Icon icon="chart-column" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={(context) => {
          if (context.user.organization.slug === 'valleyBank') return 'deny';
          if (context.user.userType === 'CAPITAL_PROVIDER') return 'deny';
          return context.flags.productsStatementsPermissions;
        }}
        GlobalNavRouterLinkItemProps={{
          to: '/statements',
          label: 'Statements',
          icon: <Icon icon="money-check-dollar" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={DeliverablesEntityConfig.permissions}
        GlobalNavRouterLinkItemProps={{
          to: '/deliverables',
          label: 'Deliverables',
          icon: <Icon icon="calendar" />,
        }}
      />

      {/* Valley Bank start */}
      <PermissionGuard permissions={ValleyBankEntityConfig.permissions}>
        <GlobalNavRouterLinkItemWithUpsell
          GlobalNavRouterLinkItemProps={{
            to: {
              pathname: '/valley-bank/loan-accounts',
              search: valleyConfig.dashboardModeQueryParam,
            },
            label: 'Loan Accounts',
            icon: <Icon icon="building-columns" />,
          }}
        />
        <GlobalNavRouterLinkItemWithUpsell
          GlobalNavRouterLinkItemProps={{
            to: {
              pathname: '/valley-bank/customers',
              search: valleyConfig.dashboardModeQueryParam,
            },
            label: 'Customers',
            icon: <Icon icon="user" />,
          }}
        />
        <GlobalNavRouterLinkItemWithUpsell
          GlobalNavRouterLinkItemProps={{
            to: {
              pathname: '/valley-bank/arrangement-groups',
              search: valleyConfig.dashboardModeQueryParam,
            },
            label: 'Arrangement Groups',
            icon: <Icon icon="folder-tree" />,
          }}
        />
        <GlobalNavRouterLinkItemWithUpsell
          permissions={() => valleyBankAssignmentPermissions(valleyConfig)}
          GlobalNavRouterLinkItemProps={{
            to: {
              pathname: '/valley-bank/assignment',
              search: valleyConfig.dashboardModeQueryParam,
            },
            label: 'Assignment',
            icon: <Icon icon="sliders" />,
          }}
        />
        <GlobalNavRouterLinkItemWithUpsell
          permissions={() => valleyBankAdvanceRatePermissions(valleyConfig)}
          GlobalNavRouterLinkItemProps={{
            to: {
              pathname: '/valley-bank/advance-rate',
              search: valleyConfig.dashboardModeQueryParam,
            },
            label: 'Advance Rate',
            icon: <Icon icon="money-check-dollar" />,
          }}
        />
      </PermissionGuard>
      {/* Valley Bank end */}

      <GlobalNavRouterLinkItemWithUpsell
        permissions={transactionsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/transactions',
          label: 'Transactions',
          icon: <Icon icon="money-check-dollar" />,
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={loansPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/loans',
          label: 'Loans',
          icon: <Icon icon="money-bill-transfer" />,
        }}
      />

      <Divider margin="0.5rem 0" width="auto" />

      <GlobalNavRouterLinkItemWithUpsell
        permissions={dataPipelinePermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/data-pipeline',
          label: 'Data Pipeline',
          icon: <Icon icon="share-nodes" />,
          size: 'sm',
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={implementationsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/implementations',
          label: 'Implementations',
          icon: <Icon icon="diagram-project" />,
          size: 'sm',
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={calcTermsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/calculation-terminologies',
          label: 'Calculation Terminologies',
          icon: <Icon icon="book" />,
          size: 'sm',
        }}
      />
      <GlobalNavRouterLinkItemWithUpsell
        permissions={eventsPermissions}
        GlobalNavRouterLinkItemProps={{
          to: '/events',
          label: 'Events Log',
          icon: <Icon icon="clock" />,
          size: 'sm',
        }}
      />
      <Show below="lg">
        <GlobalNavRouterLinkItemWithUpsell
          permissions={settingsPermissions}
          GlobalNavRouterLinkItemProps={{
            to: '/settings',
            label: 'Settings',
            icon: <Icon icon="gear" />,
            size: 'sm',
          }}
        />
      </Show>
    </GlobalNav>
  );
};
